import { useMemo } from 'react';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { useDashboardTheme, cardSx, panelHeaderSx } from '../utils';

ChartJS.register(ArcElement, Tooltip, Legend);

const COLORS = ['#e2001b', '#6366f1', '#c47d00', '#db3f57', '#1fa678', '#8b5cf6'];

function RewardDistributionPie({ liveState, loading }) {
  const t = useDashboardTheme();
  const rp = liveState?.rewardPercentages;

  const chartData = useMemo(() => {
    if (!rp) return null;
    const labels = ['1st Place', '2nd Place', '3rd Place', 'Boss', 'Airdrop', 'Jackpot'];
    const values = [
      rp.firstPlacePercentage || 0,
      rp.secondPlacePercentage || 0,
      rp.thirdPlacePercentage || 0,
      rp.bossPercentage || 0,
      rp.airdropPercentage || 0,
      rp.jackpotPercentage || 0
    ];
    const filtered = labels
      .map((l, i) => ({ label: l, value: values[i], color: COLORS[i] }))
      .filter((d) => d.value > 0);

    if (filtered.length === 0) return null;

    return {
      labels: filtered.map((d) => `${d.label} (${d.value}%)`),
      datasets: [
        {
          data: filtered.map((d) => d.value),
          backgroundColor: filtered.map((d) => d.color),
          borderColor: t.isDark ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.8)',
          borderWidth: 1
        }
      ]
    };
  }, [rp, t.isDark]);

  const opts = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: { color: t.chartLabelColor, font: { size: 11 }, padding: 12 }
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.label}: ${ctx.parsed}%`
        }
      }
    },
    cutout: '55%'
  }), [t]);

  return (
    <MDBox mb={3}>
      <Card sx={cardSx}>
        <MDBox sx={panelHeaderSx(t)}>
          <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: t.primaryText }}>
            Reward Distribution
          </MDTypography>
        </MDBox>
        <MDBox sx={{ px: { xs: 2, sm: 3 }, pb: 2, pt: 1 }}>
          {loading && !rp ? (
            <Skeleton variant='rectangular' height={220} sx={{ borderRadius: '8px' }} />
          ) : !chartData ? (
            <MDBox display='flex' flexDirection='column' alignItems='center' justifyContent='center' py={4} gap={1}>
              <MDTypography variant='body2' color='text' sx={{ opacity: 0.45 }}>
                No reward percentages configured
              </MDTypography>
            </MDBox>
          ) : (
            <Grid container spacing={2} alignItems='center'>
              <Grid item xs={12} md={6}>
                <MDBox sx={{ height: 220, position: 'relative' }}>
                  <Doughnut data={chartData} options={opts} />
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox display='flex' flexDirection='column' gap={0.5}>
                  {rp && (
                    <>
                      <MDTypography variant='caption' sx={{ color: t.secondaryText }}>
                        1st Place: <strong style={{ color: t.primaryText }}>{rp.firstPlacePercentage}%</strong>
                      </MDTypography>
                      <MDTypography variant='caption' sx={{ color: t.secondaryText }}>
                        2nd Place: <strong style={{ color: t.primaryText }}>{rp.secondPlacePercentage}%</strong>
                      </MDTypography>
                      <MDTypography variant='caption' sx={{ color: t.secondaryText }}>
                        3rd Place: <strong style={{ color: t.primaryText }}>{rp.thirdPlacePercentage}%</strong>
                      </MDTypography>
                      <MDTypography variant='caption' sx={{ color: t.secondaryText }}>
                        Boss: <strong style={{ color: t.primaryText }}>{rp.bossPercentage}%</strong>
                      </MDTypography>
                      <MDTypography variant='caption' sx={{ color: t.secondaryText }}>
                        Airdrop: <strong style={{ color: t.primaryText }}>{rp.airdropPercentage}%</strong>
                      </MDTypography>
                      <MDTypography variant='caption' sx={{ color: t.secondaryText }}>
                        Jackpot: <strong style={{ color: t.primaryText }}>{rp.jackpotPercentage}%</strong>
                      </MDTypography>
                    </>
                  )}
                </MDBox>
              </Grid>
            </Grid>
          )}
        </MDBox>
      </Card>
    </MDBox>
  );
}

export default RewardDistributionPie;
